import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FieldLabel } from './FieldLabel'
import { UserStateSelect, type UserState } from './UserStateSelect'
import { LineItemsApi, type LineItem } from '@/services/lineItems/lineItemsApi'
import { OrdersApi } from '@/services/orders/ordersApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { nextCopyName } from '@/utils/copyName'

export interface LineItemCopyDialogProps {
  lineItem: LineItem
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Ids are client-generated, matching how the rest of the app creates objects. */
function newLineItemId(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 16)
}

/**
 * Copies a line item, as the old UI's copy dialog does: a name suggested from
 * the original, the order it lands in, the state it starts in, and whether its
 * targeting and creatives come along.
 */
export function LineItemCopyDialog({ lineItem, open, onOpenChange }: LineItemCopyDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [orderId, setOrderId] = useState('')
  const [userState, setUserState] = useState<UserState>('PROPOSED')
  const [copyTargeting, setCopyTargeting] = useState(true)
  const [copyCreatives, setCopyCreatives] = useState(true)
  const [goToCopy, setGoToCopy] = useState(true)

  // Opening starts from the line item as it stands, however it was left last time.
  useEffect(() => {
    if (!open) return
    setName(nextCopyName(lineItem.name))
    setOrderId(lineItem.orderId)
    setUserState('PROPOSED')
  }, [open, lineItem.name, lineItem.orderId])

  /** The orders the copy can land in — the ones this advertiser has. */
  const { data: orders = [] } = useQuery({
    queryKey: ['advertiser-orders', lineItem.advertiserId],
    queryFn: () => OrdersApi.getByAdvertiser(lineItem.advertiserId),
    enabled: open && !!lineItem.advertiserId,
    staleTime: 5 * 60 * 1000,
  })

  const copy = useMutation({
    mutationFn: () =>
      LineItemsApi.copy(lineItem.id, {
        newId: newLineItemId(),
        name: name.trim(),
        orderId,
        userState,
        copyTargeting,
        copyCreatives,
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['lineitems'] })
      queryClient.invalidateQueries({ queryKey: ['order-lineitems'] })
      toast.success(t('lineItems.copied', { name: created.name }))
      onOpenChange(false)
      if (goToCopy) navigate(`/advertising/lineitems/${created.id}`)
    },
    onError: (error) =>
      toast.error(t('lineItems.copyFailed'), { description: apiErrorDescription(error) }),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('lineItems.copyTitle')}</DialogTitle>
          <DialogDescription>{lineItem.name}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <FieldLabel>{t('common.name')}</FieldLabel>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel>{t('lineItems.copyOrder')}</FieldLabel>
            <Select value={orderId} onValueChange={setOrderId}>
              <SelectTrigger>
                <SelectValue>
                  {orders.find((order) => order.id === orderId)?.name ?? lineItem.orderName}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {orders.map((order) => (
                  <SelectItem key={order.id} value={order.id}>
                    {order.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel>{t('lineItems.copyState')}</FieldLabel>
            <UserStateSelect value={userState} onChange={setUserState} />
          </div>

          <div className="flex flex-col gap-2">
            <FieldLabel>{t('lineItems.copyInclude')}</FieldLabel>
            <Label className="font-normal">
              <Checkbox
                checked={copyTargeting}
                onCheckedChange={(value) => setCopyTargeting(!!value)}
              />
              {t('targeting.title')}
            </Label>
            <Label className="font-normal">
              <Checkbox
                checked={copyCreatives}
                onCheckedChange={(value) => setCopyCreatives(!!value)}
              />
              {t('creatives.title')}
            </Label>
          </div>

          <Label className="font-normal">
            <Checkbox checked={goToCopy} onCheckedChange={(value) => setGoToCopy(!!value)} />
            {t('lineItems.copyGoTo')}
          </Label>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            loading={copy.isPending}
            disabled={!name.trim()}
            onClick={() => copy.mutate()}
          >
            <Copy className="size-4" />
            {t('common.copy')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
